import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  EnvelopeIcon,
  CalendarIcon,
  PhoneIcon,
  MapPinIcon,
  ClockIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

const ContactPage = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Simulate form submission
    setIsSubmitted(true);
    setTimeout(() => setIsSubmitted(false), 3000);
    setFormData({ name: '', email: '', company: '', message: '' });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const contactInfo = [
    {
      icon: EnvelopeIcon,
      title: "Email Us",
      content: "contact@valeon.io",
      description: "We'll respond within 24 hours"
    },
    {
      icon: CalendarIcon,
      title: "Schedule a Call",
      content: "Book 30-min consultation",
      description: "Free initial assessment"
    },
    {
      icon: MapPinIcon,
      title: "Global Presence",
      content: "New York • London • Singapore",
      description: "Serving clients worldwide"
    },
    {
      icon: ClockIcon,
      title: "Response Time",
      content: "< 24 hours",
      description: "Quick turnaround guaranteed"
    }
  ];

  return (
    <div className="min-h-screen bg-primary-900 pt-20">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-b from-primary-800 to-primary-900">
        <div className="section-padding">
          <div className="container-width">
            <motion.div
              ref={ref}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <div className="inline-block px-6 py-2 glass-card text-accent-gold text-sm font-medium tracking-wide uppercase mb-6">
                Get In Touch
              </div>
              <h1 className="text-display font-serif font-bold text-cream-100 mb-6">
                Let's Talk About Your{' '}
                <span className="text-gradient">Liquidity Strategy</span>
              </h1>
              <p className="text-body-lg text-cream-200 max-w-3xl mx-auto">
                Ready to optimize your token's liquidity performance? Our team is here to help 
                you navigate the complexities of modern token markets with expert guidance.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Form and Info */}
      <section className="py-16 bg-primary-900">
        <div className="section-padding">
          <div className="container-width">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              {/* Contact Form */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="glass-card p-8"
              >
                <h2 className="text-heading-lg font-serif font-bold text-cream-100 mb-6">
                  Send us a Message
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-cream-200 text-sm font-medium mb-2">
                        Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-cream-100 placeholder-cream-400 focus:outline-none focus:ring-2 focus:ring-accent-blue/50 focus:border-accent-blue/50 transition-all duration-300"
                        placeholder="Your full name"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="email" className="block text-cream-200 text-sm font-medium mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-cream-100 placeholder-cream-400 focus:outline-none focus:ring-2 focus:ring-accent-blue/50 focus:border-accent-blue/50 transition-all duration-300"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="company" className="block text-cream-200 text-sm font-medium mb-2">
                      Company
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-cream-100 placeholder-cream-400 focus:outline-none focus:ring-2 focus:ring-accent-blue/50 focus:border-accent-blue/50 transition-all duration-300"
                      placeholder="Your company name"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="message" className="block text-cream-200 text-sm font-medium mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={6}
                      value={formData.message}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-cream-100 placeholder-cream-400 focus:outline-none focus:ring-2 focus:ring-accent-blue/50 focus:border-accent-blue/50 transition-all duration-300 resize-none"
                      placeholder="Tell us about your token liquidity challenges or questions..."
                    />
                  </div>
                  
                  <button
                    type="submit"
                    disabled={isSubmitted}
                    className="w-full btn-primary relative overflow-hidden"
                  >
                    {isSubmitted ? (
                      <span className="flex items-center justify-center">
                        <CheckCircleIcon className="h-5 w-5 mr-2" />
                        Message Sent!
                      </span>
                    ) : (
                      'Send Message'
                    )}
                  </button>
                </form>
                
                <div className="mt-8 pt-6 border-t border-white/10">
                  <div className="flex items-center space-x-3 text-cream-300">
                    <EnvelopeIcon className="h-5 w-5 text-accent-blue" />
                    <div>
                      <div className="text-sm font-medium text-cream-200">Direct Email</div>
                      <a href="mailto:contact@valeon.io" className="text-accent-blue hover:text-accent-gold transition-colors duration-300">
                        contact@valeon.io
                      </a>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Contact Information */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: 30 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="space-y-8"
              >
                <div>
                  <h2 className="text-heading-lg font-serif font-bold text-cream-100 mb-6">
                    Get in Touch
                  </h2>
                  <p className="text-cream-200 leading-relaxed mb-8">
                    Whether you're looking to optimize existing liquidity arrangements, 
                    evaluate market maker performance, or develop a comprehensive token 
                    strategy, our team is ready to help.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {contactInfo.map((item, index) => (
                    <motion.div
                      key={item.title}
                      initial={{ opacity: 0, y: 20 }}
                      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                      transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
                      className="glass-card p-6 group hover:scale-105 transition-transform duration-300"
                    >
                      <div className="mb-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-accent-blue to-accent-gold rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                          <item.icon className="h-6 w-6 text-white" />
                        </div>
                      </div>
                      <h3 className="text-cream-100 font-semibold mb-2">{item.title}</h3>
                      <div className="text-accent-blue font-medium mb-1">{item.content}</div>
                      <div className="text-cream-300 text-sm">{item.description}</div>
                    </motion.div>
                  ))}
                </div>

                {/* Book a Call CTA */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                  transition={{ duration: 0.6, delay: 1.0 }}
                  className="glass-card p-8 text-center"
                >
                  <div className="mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-accent-blue to-accent-gold rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <CalendarIcon className="h-8 w-8 text-white" />
                    </div>
                  </div>
                  
                  <h3 className="text-heading-md font-serif font-bold text-cream-100 mb-4">
                    Schedule a Consultation
                  </h3>
                  
                  <p className="text-cream-200 mb-6">
                    Book a 30-minute call with our team to discuss your specific 
                    liquidity challenges and optimization opportunities.
                  </p>
                  
                  <button className="btn-primary">
                    Book a Call
                    <CalendarIcon className="h-4 w-4 ml-2" />
                  </button>
                  
                  <p className="text-cream-400 text-xs mt-4">
                    Free initial assessment • No commitment required
                  </p>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gradient-to-b from-primary-900 to-primary-800">
        <div className="section-padding">
          <div className="container-width">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-12"
            >
              <h2 className="text-heading-lg font-serif font-bold text-cream-100 mb-6">
                Frequently Asked Questions
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {[
                {
                  question: "What's included in a liquidity health check?",
                  answer: "A comprehensive analysis of your token's market structure, including depth, spread, slippage analysis, and benchmarking against industry standards."
                },
                {
                  question: "How long does an engagement typically take?",
                  answer: "Most assessments are completed within 2-3 weeks, while ongoing advisory relationships can be customized to your needs."
                },
                {
                  question: "Do you work with early-stage projects?",
                  answer: "Yes, we work with projects at all stages, from pre-launch planning to established tokens looking to optimize performance."
                },
                {
                  question: "What makes Valeon different?",
                  answer: "Our unique combination of traditional finance expertise, crypto-native knowledge, and proprietary analytics delivers institutional-grade insights."
                }
              ].map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="glass-card p-6"
                >
                  <h3 className="text-cream-100 font-semibold mb-3">{faq.question}</h3>
                  <p className="text-cream-300 text-sm leading-relaxed">{faq.answer}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;